/**
 * @cfleet/api-client - Password Reset Hooks
 *
 * React Query hooks for password reset operations.
 * These are public endpoints (no auth required).
 *
 * @example
 * ```tsx
 * import { useRequestPasswordReset, useConfirmPasswordReset } from '@cfleet/api-client';
 *
 * // Request password reset
 * const { mutate: requestReset, isPending } = useRequestPasswordReset();
 * requestReset({ email: 'user@example.com' }, {
 *   onSuccess: () => toast.success('Check your email for reset instructions'),
 * });
 *
 * // Confirm password reset (with token from email)
 * const { mutate: confirmReset } = useConfirmPasswordReset();
 * confirmReset({ token, new_password: newPassword }, {
 *   onSuccess: () => navigate('/login'),
 * });
 * ```
 */

import { useMutation } from '@tanstack/react-query';
import type { AuthComponents } from '../generated';
import { createCoreClient, type ClientConfig } from '../index';

// ─────────────────────────────────────────────────────────────────────────────
// Type Aliases
// ─────────────────────────────────────────────────────────────────────────────

type PasswordResetRequest = AuthComponents['schemas']['PasswordResetRequest'];
type PasswordResetResponse = AuthComponents['schemas']['PasswordResetResponse'];
type PasswordResetConfirm = AuthComponents['schemas']['PasswordResetConfirm'];
type PasswordResetConfirmResponse = AuthComponents['schemas']['PasswordResetConfirmResponse'];
type ErrorResponse = AuthComponents['schemas']['ErrorResponse'];

// ─────────────────────────────────────────────────────────────────────────────
// Configuration
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Configuration for password reset hooks
 */
export interface UsePasswordResetHooksConfig {
  /** Custom client configuration (optional) */
  clientConfig?: ClientConfig;
}

// ─────────────────────────────────────────────────────────────────────────────
// useRequestPasswordReset
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to request a password reset email.
 *
 * This is a public endpoint (no auth required).
 * An email with reset instructions will be sent to the address if it exists.
 * For security, the response is always successful even if the email doesn't exist.
 *
 * @param config - Optional configuration including custom client config
 * @returns Mutation hook for requesting password reset
 *
 * @example
 * ```tsx
 * const { mutate: requestReset, isPending, error, isSuccess } = useRequestPasswordReset();
 *
 * const handleSubmit = (email: string) => {
 *   requestReset({ email }, {
 *     onSuccess: () => {
 *       // Show "check your email" message
 *       setShowCheckEmail(true);
 *     },
 *     onError: (error) => {
 *       // Handle error (e.g., rate limiting)
 *       toast.error(error.message);
 *     },
 *   });
 * };
 * ```
 */
export function useRequestPasswordReset(config?: UsePasswordResetHooksConfig) {
  const client = createCoreClient(config?.clientConfig);

  return useMutation({
    mutationFn: async (data: PasswordResetRequest): Promise<PasswordResetResponse> => {
      const { data: response, error } = await client.POST('/auth/password/reset', {
        body: data,
      });

      // Note: This endpoint always returns 202 per OpenAPI spec (no error responses defined)
      // If there's an error, it's likely a network error or server issue
      if (error) {
        // Error type is 'never' since no error responses are defined in OpenAPI
        // Cast to unknown first for safety
        const errorObj = error as unknown;
        if (errorObj && typeof errorObj === 'object' && 'detail' in errorObj) {
          throw new Error((errorObj as ErrorResponse).detail || 'Failed to request password reset');
        }
        throw new Error('Failed to request password reset');
      }

      return response!;
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useConfirmPasswordReset
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to confirm a password reset with token and new password.
 *
 * This is a public endpoint (no auth required).
 * The token is obtained from the password reset email link.
 *
 * @param config - Optional configuration including custom client config
 * @returns Mutation hook for confirming password reset
 *
 * @example
 * ```tsx
 * const { mutate: confirmReset, isPending, error } = useConfirmPasswordReset();
 *
 * const handleSubmit = (token: string, newPassword: string) => {
 *   confirmReset({ token, new_password: newPassword }, {
 *     onSuccess: () => {
 *       toast.success('Password reset successfully!');
 *       navigate('/login');
 *     },
 *     onError: (error) => {
 *       // Handle error (e.g., expired token, weak password)
 *       toast.error(error.message);
 *     },
 *   });
 * };
 * ```
 */
export function useConfirmPasswordReset(config?: UsePasswordResetHooksConfig) {
  const client = createCoreClient(config?.clientConfig);

  return useMutation({
    mutationFn: async (data: PasswordResetConfirm): Promise<PasswordResetConfirmResponse> => {
      const { data: response, error } = await client.POST('/auth/password/reset/confirm', {
        body: data,
      });

      if (error) {
        // Error is defined in OpenAPI (400 response) with ErrorResponse schema
        const errorObj = error as ErrorResponse;
        throw new Error(errorObj.detail || 'Failed to reset password');
      }

      return response!;
    },
  });
}
